import { useEffect, useState } from "react";
import { Check, Facebook, Instagram, Link2, Linkedin, MessageSquare, QrCode, Share2, Mail } from "lucide-react";
import { toast } from "sonner";

import {
  DEFAULT_SHARE_CHANNELS,
  defaultShareMessage,
  type ShareChannel,
} from "@/lib/share-channels";
import { track } from "@/lib/analytics";
import { qrShortUrl } from "@/lib/qr-links";
import { taggedUrl } from "@/lib/analytics-shared";

const CHANNEL_SOURCE: Record<ShareChannel, string> = {
  copy: "copy",
  sms: "sms",
  email: "email",
  facebook: "fb",
  x: "x",
  linkedin: "li",
  whatsapp: "wa",
  instagram: "ig",
  tiktok: "tiktok",
};

export function ShareEvent({
  title,
  url,
  channels,
  message: messageOverride,
  autoOpen,
  slug,
  inline,
}: {
  title: string;
  url?: string;
  channels?: string[] | null;
  message?: string | null;
  autoOpen?: ShareChannel | null;
  slug?: string;
  inline?: boolean;

}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [href, setHref] = useState(url ?? "");
  const [canNativeShare, setCanNativeShare] = useState(false);
  const [isTouch, setIsTouch] = useState(true);

  useEffect(() => {
    const resolved = url ?? window.location.href;
    setHref(resolved);
    setCanNativeShare(typeof navigator !== "undefined" && typeof navigator.share === "function");
    setIsTouch(window.matchMedia?.("(pointer: coarse)").matches ?? false);
  }, [url]);

  const enabled = (channels && channels.length ? channels : DEFAULT_SHARE_CHANNELS) as ShareChannel[];
  const has = (c: ShareChannel) => enabled.includes(c);

  const message = (messageOverride?.trim() || defaultShareMessage(title, href)).replace(
    "{url}",
    href,
  );
  const messageWithLink = message.includes(href) ? message : `${message} ${href}`;
  const headline = messageOverride?.trim() || `Volunteer with T1D Warriors at ${title}.`;
  const enc = encodeURIComponent;

  const linkFor = (channel: ShareChannel) =>
    href ? taggedUrl(href, CHANNEL_SOURCE[channel] ?? channel) : href;
  const messageFor = (channel: ShareChannel) =>
    href ? messageWithLink.split(href).join(linkFor(channel)) : messageWithLink;
  const trackShare = (channel: ShareChannel) =>
    track("share_channel", { slug: slug ?? null, channel });


  useEffect(() => {
    if (!autoOpen || !href) return;
    setOpen(true);
    if (autoOpen === "sms" && !isTouch) {
      void copyText(messageWithLink, "Message copied. Paste it into your texting app.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen, href, isTouch]);

  async function copyText(text: string, successMessage: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
      return true;
    } catch {
      toast.error("Could not copy. Select the link in the address bar instead.");
      return false;
    }
  }

  async function nativeShare() {
    track("share_native", { slug: slug ?? null, channel: "native" });
    try {
      await navigator.share({ title, text: headline, url: linkFor("copy") });
    } catch {
      setOpen(true);
    }
  }

  async function copy() {
    trackShare("copy");
    const ok = await copyText(linkFor("copy"), "Link copied. Paste it anywhere.");
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  async function openWithCaption(channel: ShareChannel, target: string) {
    trackShare(channel);
    await copyText(messageFor(channel), "Caption copied. Paste it into your post.");
    window.open(target, "_blank", "noopener,noreferrer");
  }


  const qrShareUrl = slug ? qrShortUrl(`e-${slug}`) : taggedUrl(href, "qr");

  async function showQr() {
    if (!href) return;
    setQrOpen(true);
    if (qrImage) return;
    try {
      // Loaded on demand so the public page stays light.
      const { default: QRCodeLib } = await import("qrcode");
      const image = await QRCodeLib.toDataURL(qrShareUrl, {
        errorCorrectionLevel: "M",
        margin: 4,
        width: 1024,
        color: { dark: "#0a1f2e", light: "#ffffff" },
      });
      setQrImage(image);
    } catch {
      toast.error("Could not build the QR code just now.");
      setQrOpen(false);
    }
  }

  if (!enabled.length) return null;

  const qrPanel = qrOpen ? (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground/50 p-4">
      <button
        type="button"
        aria-label="Close QR code"
        className="absolute inset-0 cursor-default"
        onClick={() => setQrOpen(false)}
      />
      <div className="relative w-full max-w-sm rounded-sm border border-border bg-popover p-5 text-center shadow-lg">
        <p className="text-sm font-semibold text-popover-foreground">Scan to open this signup page</p>
        <div className="mx-auto mt-4 w-fit bg-white p-3" style={{ borderRadius: 4 }}>
          {qrImage ? (
            <img src={qrImage} alt={`QR code for ${title}`} width={220} height={220} />
          ) : (
            <div className="flex h-[220px] w-[220px] items-center justify-center text-muted-foreground">
              <QrCode className="h-8 w-8" />
            </div>
          )}
        </div>
        <p className="mt-3 break-all text-xs text-muted-foreground">
          {href ? qrShareUrl : ""}
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <a
            href={qrImage ?? "#"}
            download={`${slug || "event"}-qr.png`}
            className="inline-flex min-h-11 items-center rounded-sm border border-cyan/40 px-4 text-sm font-semibold text-cyan hover:bg-cyan/10"
          >
            Download PNG
          </a>
          <button
            type="button"
            onClick={() => setQrOpen(false)}
            className="inline-flex min-h-11 items-center rounded-sm border border-border px-4 text-sm font-semibold text-popover-foreground hover:bg-muted"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const items = (
    <>
      {has("copy") ? (

              <ShareItem
                icon={copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
                label={copied ? "Link copied" : "Copy link"}
                onClick={copy}
              />
            ) : null}
            <ShareItem
              icon={<QrCode className="h-4 w-4" />}
              label="QR code"
              onClick={() => {
                track("share_channel", { slug: slug ?? null, channel: "qr" });
                void showQr();
              }}
            />
            {has("sms") ? (
              isTouch ? (
                <ShareItem
                  icon={<MessageSquare className="h-4 w-4" />}
                  label="Text message"
                  href={`sms:?&body=${enc(messageFor("sms"))}`}
                  onClick={() => trackShare("sms")}
                />
              ) : (
                <ShareItem
                  icon={<MessageSquare className="h-4 w-4" />}
                  label="Copy text message"
                  onClick={() => {
                    trackShare("sms");
                    void copyText(
                      messageFor("sms"),
                      "Message copied. Paste it into your texting app.",
                    );
                  }}
                />
              )
            ) : null}
            {has("email") ? (
              <ShareItem
                icon={<Mail className="h-4 w-4" />}
                label="Email"
                href={`mailto:?subject=${enc(`Volunteer with T1D Warriors: ${title}`)}&body=${enc(messageFor("email"))}`}
                onClick={() => trackShare("email")}
              />
            ) : null}
            {has("facebook") ? (
              <ShareItem
                icon={<Facebook className="h-4 w-4" />}
                label="Facebook"
                href={`https://www.facebook.com/sharer/sharer.php?u=${enc(linkFor("facebook"))}`}
                onClick={() => trackShare("facebook")}
                external
              />
            ) : null}
            {has("x") ? (
              <ShareItem
                icon={<XIcon />}
                label="X"
                href={`https://twitter.com/intent/tweet?text=${enc(headline)}&url=${enc(linkFor("x"))}`}
                onClick={() => trackShare("x")}
                external
              />
            ) : null}
            {has("linkedin") ? (
              <ShareItem
                icon={<Linkedin className="h-4 w-4" />}
                label="LinkedIn"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(linkFor("linkedin"))}`}
                onClick={() => trackShare("linkedin")}
                external
              />
            ) : null}
            {has("whatsapp") ? (
              <ShareItem
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                href={`https://wa.me/?text=${enc(messageFor("whatsapp"))}`}
                onClick={() => trackShare("whatsapp")}
                external
              />
            ) : null}
            {has("instagram") ? (
              <ShareItem
                icon={<Instagram className="h-4 w-4" />}
                label="Instagram"
                onClick={() => openWithCaption("instagram", "https://www.instagram.com/")}
              />
            ) : null}
            {has("tiktok") ? (
              <ShareItem
                icon={<TikTokIcon />}
                label="TikTok"
                onClick={() => openWithCaption("tiktok", "https://www.tiktok.com/upload")}
              />
            ) : null}
    </>
  );

  if (inline) {
    return (
      <div className="grid gap-1 sm:grid-cols-2" role="group" aria-label={`Share ${title}`}>
        {canNativeShare ? (
          <ShareItem
            icon={<Share2 className="h-4 w-4" />}
            label="Share now"
            onClick={() => void nativeShare()}
          />
        ) : null}
        {items}
        {qrPanel}
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        type="button"
        aria-label={`Share ${title}`}
        onClick={() => {
          if (canNativeShare) {
            void nativeShare();
            return;
          }
          setOpen((v) => {
            if (!v) track("share_opened", { slug: slug ?? null });
            return !v;
          });
        }}
        className="inline-flex min-h-11 items-center gap-2 border border-cyan/40 px-4 text-sm font-semibold text-cyan transition-colors hover:bg-cyan/10"
        style={{ borderRadius: 4 }}
      >
        <Share2 className="h-4 w-4" />
        Share
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-label="Close share menu"
            className="fixed inset-0 z-40 cursor-default bg-foreground/20 sm:bg-transparent"
            onClick={() => setOpen(false)}
          />
          <div
            className="fixed inset-x-0 bottom-0 z-50 max-h-[75vh] overflow-y-auto border-t border-border bg-popover p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-lg sm:absolute sm:inset-x-auto sm:bottom-auto sm:left-0 sm:mt-2 sm:w-60 sm:rounded-sm sm:border"
          >
            {items}
          </div>
        </>
      ) : null}
      {qrPanel}
    </div>
  );
}


function ShareItem({
  icon,
  label,
  href,
  external,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
}) {
  const className =
    "flex min-h-12 w-full items-center gap-3 rounded-sm px-3 text-left text-base font-medium text-popover-foreground transition-colors hover:bg-cyan/10 hover:text-cyan sm:min-h-11 sm:text-sm";
  if (href) {
    return (
      <a
        href={href}
        onClick={onClick}
        className={className}
        {...(external ? { target: "_blank", rel: "noreferrer noopener" } : {})}
      >
        <span className="text-cyan">{icon}</span>
        {label}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={className}>
      <span className="text-cyan">{icon}</span>
      {label}
    </button>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2H21.5l-7.5 8.57L22.5 22h-6.9l-4.6-6.02L5.6 22H2.34l8.02-9.17L1.5 2h7.07l4.16 5.5L18.244 2Zm-1.14 18h1.83L7.02 3.9H5.06l12.044 16.1Z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.28-1.38a9.9 9.9 0 0 0 4.76 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.95 6.45 17.5 2 12.04 2Zm5.8 14.03c-.24.68-1.4 1.3-1.94 1.35-.5.05-1.12.07-1.81-.11-.42-.11-.95-.29-1.64-.59-2.89-1.25-4.77-4.16-4.92-4.36-.14-.19-1.17-1.56-1.17-2.97 0-1.41.74-2.11 1-2.4.26-.29.57-.36.76-.36.19 0 .38 0 .55.01.18.01.41-.07.64.49.24.57.81 1.98.88 2.13.07.14.12.31.02.5-.09.19-.14.31-.28.48-.14.16-.29.36-.42.49-.14.14-.28.29-.12.57.16.29.71 1.17 1.53 1.9 1.05.94 1.94 1.23 2.22 1.37.28.14.44.12.6-.07.17-.19.69-.8.88-1.08.19-.29.37-.24.62-.14.26.09 1.66.78 1.94.92.28.14.47.21.54.33.07.11.07.66-.17 1.34Z" />
    </svg>
  );
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.1v12.3a2.6 2.6 0 1 1-1.86-2.49v-3.15a5.7 5.7 0 1 0 4.96 5.65V9.4a7.36 7.36 0 0 0 4.3 1.38V7.7a4.3 4.3 0 0 1-3.24-1.88Z" />
    </svg>
  );
}
